<template>
  <div class="py-6 px-8">
    <h-header />

    <DisputeListFilters />

    <v-card outlined>
      <v-data-table
        @dblclick:row="showDisputeRow"
        :loading-text="$t('dispute.table.loading')"
        :no-data-text="$t('dispute.table.noData')"
        :items="disputes"
        :loading="isLoading"
        :items-per-page="15"
        :headers="headers"
        item-key="index"
        mobile-breakpoint="20"
        class="clickable"
        hide-default-footer
        disable-sort
      >
        <template v-slot:[`item.jobCode`]="{ item }" ]>
          <span class="font-weight-bold">
            {{ item.jobCode }}
          </span>
        </template>
        <template v-slot:[`item.type`]="{ item }">
          {{ $t(`dispute.type.${item.type}`) }}
        </template>
        <template v-slot:[`item.assetCode`]="{ item }">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <div class="my-2 d-flex align-start gap-1 align-center" v-on="on">
                <div>
                  <v-chip class="caption text-truncate mw-100" small>
                    {{ item.assetCode }}
                  </v-chip>
                </div>
                <p class="mb-0 text-ellipsis text-truncate mw-300">
                  {{ item.assetAddress }}
                </p>
              </div>
            </template>
            <span small>
              <span class="font-weight-bold">
                {{ item.assetCode }}
              </span>
              <span> - {{ item.assetAddress }} </span>
            </span>
          </v-tooltip>
        </template>
        <template v-slot:[`item.host`]="{ item }">
          {{ item.hostName }}
        </template>
        <template v-slot:[`item.createdDate`]="{ item }">
          <span class="caption">
            {{ formatDate(item.createdDate) }}
          </span>
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <v-chip :class="formatColors(getDisputeStatus(item))" small>
            {{ $t(`dispute.status.${getDisputeStatus(item)}`) }}
          </v-chip>
        </template>
        <template v-slot:[`item.ownerType`]="{ item }">
          <v-chip
            v-if="item.ownerType"
            :class="ownerTypeClass(item.ownerType)"
            small
          >
            {{ $t(`dispute.ownerType.${item.ownerType}`) }}
          </v-chip>
          <span v-else>-</span>
        </template>
        <template v-slot:[`item.slaRemainingMinutes`]="{ item }">
          <v-tooltip v-if="item.hasOverdueItems" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon color="error" small v-bind="attrs" v-on="on">mdi-clock-alert</v-icon>
            </template>
            <span>{{ $t('dispute.sla.hasOverdueItems') }}</span>
          </v-tooltip>
          <v-tooltip v-else-if="item.pendingItemsQuantity > 0" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon color="success" small v-bind="attrs" v-on="on">mdi-clock-check-outline</v-icon>
            </template>
            <span>{{ $t('dispute.sla.withinDeadline') }}</span>
          </v-tooltip>
          <v-tooltip v-else bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon color="grey lighten-1" small v-bind="attrs" v-on="on">mdi-check-circle-outline</v-icon>
            </template>
            <span>{{ $t('dispute.sla.finished') }}</span>
          </v-tooltip>
        </template>
        <template v-slot:[`item.pendingItemsQuantity`]="{ item }">
          <ShowDisputeItemsStatus :item="item" />
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex justify-end">
            <v-btn @click="showDispute(item.appointmentId)" icon small>
              <v-icon small> mdi-eye </v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <h-pagination :fetch="fetchDisputes" />
  </div>
</template>

<script>
import { catalogDate } from '@/util/catalog'
import { handleColors } from '@/util/colors'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { headers } from './disputeHeaders'

import DisputeListFilters from './components/DisputeListFilters'
import ShowDisputeItemsStatus from './fragments/showDisputeItemsStatus'

import router from '@/router'

export default {
  name: 'DisputeList',

  components: {
    DisputeListFilters,
    ShowDisputeItemsStatus,
  },

  computed: {
    ...mapGetters([
      'disputes',
      'filters',
      'isLoading',
      'serviceProviderCustomers',
    ]),

    headers() {
      if (this.isAdmin) return headers
      return headers.filter(h => h.value !== 'slaRemainingMinutes')
    },

    roles() {
      const roles = localStorage.getItem('access_roles')
      if (!roles) return []
      return roles.split(',')
    },

    isAdmin() {
      return this.roles.includes('ROLE_ADMIN')
    },

    isServiceProvider() {
      return this.roles.includes('ROLE_SERVICE_PROVIDER')
    },

    isHostRoute() {
      return this.$route?.meta?.roles?.includes('ROLE_HOST')
    },
  },

  methods: {
    ...mapActions([
      'fetchAppointment',
      'fetchDisputes',
      'listServiceProviderCustomers',
      'setFilters',
    ]),

    ...mapMutations([
      'setIsAppointmentLoading',
      'setIsLoading',
      'setFilters',
    ]),

    formatDate: catalogDate,
    formatColors: handleColors,

    ownerTypeClass(ownerType) {
      if (ownerType === 'SERVICE_PROVIDER') return 'deep-orange lighten-4'
      if (ownerType === 'HAUSEFUL') return 'blue-grey lighten-4'
      return 'light-blue lighten-4'
    },

    showDispute(appointmentId) {
      this.$store.commit('setIsDisputeDrawerOpen', true)
      this.fetchAppointment(appointmentId).finally(() =>
        this.setIsAppointmentLoading(false)
      )
    },

    showDisputeRow(_, { item }) {
      this.showDispute(item?.appointmentId)
    },

    getDisputeStatus(item) {
      const isFinished = item.finishDate
      const hasNoPendingItems = !item.pendingItemsQuantity || item.pendingItemsQuantity == 0

      if (isFinished && hasNoPendingItems) return 'FINISHED'
      return 'PENDING'
    },

    async setCustomerIds() {
      const hasNotCustomers = this.serviceProviderCustomers.length === 0
      if (hasNotCustomers) await this.listServiceProviderCustomers()

      const companiesIds = this.serviceProviderCustomers.map(customer => customer.company_id)
      this.setFilters({
        ...this.filters,
        companiesIds,
        ...(this.isHostRoute && { type: 'AUDIT' }),
      })
    },

    async init() {
      const disputeId = router.currentRoute.query.id
      disputeId && this.showDispute(disputeId)

      if (this.isServiceProvider) {
        await this.setCustomerIds()
      }

      if (this.isHostRoute) {
        this.setFilters({ ...this.filters, type: 'AUDIT' })
      }

      this.fetchDisputes()
    }
  },

  mounted() {
    this.init()
  },
}
</script>
